import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import InputSettingsField from './InputSettingsField';
import Icon from '../../misc/Icon';
import PopoverMenu from '../../misc/PopoverMenu';
import Button from '../../misc/Button';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const StyledPopoverHeader = styled.div`
  padding: 10px 10px 0px 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledActionsWrapper = styled.div`
  padding: 8px 12px 12px 12px;
  flex-direction: row;
  display: flex;
  align-items: flex-end;
`;

class IdentifierSelectorSettings extends React.PureComponent {

  static contextType = ThemeContext

  static propTypes = {
    fieldId: PropTypes.string,
    type: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.any,
    disabled: PropTypes.bool,
    inputStyles: PropTypes.object,
    onChange: PropTypes.func,
    enableCustomOption: PropTypes.bool
  }

  static defaultProps = {
    value: '',
    inputStyles: {},
  }

  constructor(props) {
    super(props);
    const { value } = props;
    this.state = { focused: false, changes: value, searchQuery: '' };
  }

  componentDidUpdate(prevProps) {
    const { changes } = this.state;
    const { value } = this.props;
    if (value !== prevProps.value && value !== changes) this.setState({ changes: value });
  }

  componentWillUnmount() {

    const { value, onChange, fieldId } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== value) {
      onChange(fieldId, changes);
    }

  }

  render() {

    const { changes, focused, displayPopover, searchQuery } = this.state;

    const {
      fieldId,
      type,
      label,
      disabled,
      onChange,
      inputStyles,

      // value:String - customValue:Object (can only be one or the other)
      value,
      customValue,
      enableCustomOption
    } = this.props;

    const { fontColorPrimary } = this.context;

    const nextInputStyles = { ...inputStyles };

    if (value) nextInputStyles.maxWidth = '125px';

    const identifiers = customValue ? customValue.map((i) => {

      if (searchQuery.length > 0 && i.title.toLowerCase().indexOf(searchQuery.toLowerCase()) === -1) return null;

      return {
        textStyles: { fontWeight: 500 },
        subTextStyles: { fontWeight: 400 },
        text: i.title,
        subtext: i.description,
        value: i.value,
        active: i.value === changes,
        onClick: () => {
          this.handleSave(i.value);
        }
      };
    }).filter((i) => !!i) : null;

    const emptyIdentifiers = identifiers && identifiers.length === 0;

    if (!customValue || (emptyIdentifiers && searchQuery.length === 0)) {
      return (
        <InputSettingsField
          fieldId={fieldId}
          disabled={disabled}
          label={label}
          type={type}
          value={value}
          style={inputStyles}
          addonBefore={<Icon icon={ThemeIcons.settings.identifier} size="sm" />}
          onChange={(fieldId, value) => onChange(fieldId, value)}
        />
      );
    }

    return (
      <StyledWrapper>
        {customValue && (
          <PopoverMenu
            isOpen={displayPopover}
            positions={['left']} // preferred positions by priority
            padding={20}
            containerStyles={{ maxHeight: '320px', overflowY: 'scroll' }}
            items={identifiers}
            onClickOutside={() => {
              if (!focused && displayPopover) this.setState({ displayPopover: false, searchQuery: '' });
            }}
            header={(
              <StyledPopoverHeader>
                <InputSettingsField
                  disabled={disabled}
                  onChangeText={(searchQuery) => this.setState({ searchQuery })}
                  addonBefore={<Icon color={fontColorPrimary} icon={ThemeIcons.misc.search} size="sm" />}
                />
              </StyledPopoverHeader>
            )}
            content={enableCustomOption !== false ? (
              <StyledActionsWrapper>
                <InputSettingsField
                  label="Create Custom"
                  disabled={disabled}
                  onChangeText={(changes) => this.setState({ changes })}
                />
                <Button
                  styles={{ maxHeight: '40px', marginLeft: '6px' }}
                  text="Save"
                  onClick={() => this.handleSave(changes)}
                />
              </StyledActionsWrapper>
            ) : null}
          >
            <div id="identifier-selector" />
          </PopoverMenu>
        )}
        <InputSettingsField
          label={label}
          disabled={disabled}
          value={changes}
          inputStyles={nextInputStyles}
          addonBefore={<Icon icon={ThemeIcons.settings.identifier} size="sm" />}
          onFocus={() => {
            if (customValue && !displayPopover) this.handleTogglePopover();
            if (!focused) this.setState({ focused: true });
          }}
          onBlur={() => {
            if (focused) this.setState({ focused: false });
          }}
          onClear={() => {
            this.setState({ changes: '' });
            onChange(fieldId, '');
          }}
        />
      </StyledWrapper>
    );

  }

  handleSave = (val) => {
    const { focused } = this.state;

    const {
      fieldId,
      type,
      onChange,
      // value:String - customValue:Object (can only be one or the other)
      value,
    } = this.props;

    const nextValue = type === 'number' ? parseInt(val, 10) : val;
    if (focused) this.setState({ focused: false });
    this.setState({ displayPopover: false, changes: nextValue });
    if (nextValue !== value) onChange(fieldId, nextValue);

  }

  handleTogglePopover = () => {
    const { displayPopover } = this.state;
    this.setState({ displayPopover: !displayPopover });
  }

}

export default IdentifierSelectorSettings;
